import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@repo/api-client";
import type { Book, WisdomVideo, BookReview } from "@repo/shared-types";

type Tab = "books" | "videos";

const BOOK_CATEGORIES = ["All", "memoir", "philosophy", "self-help", "poetry", "interfaith", "astrology"];
const VIDEO_CATEGORIES = ["All", "teaching", "meditation", "talk", "interview", "ritual", "music"];

export default function LibraryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>((searchParams.get("tab") as Tab) || "books");
  const [books, setBooks] = useState<Book[]>([]);
  const [videos, setVideos] = useState<WisdomVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [bookCategory, setBookCategory] = useState("All");
  const [videoCategory, setVideoCategory] = useState("All");
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [reviews, setReviews] = useState<BookReview[]>([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [{ books: nextBooks }, { videos: nextVideos }] = await Promise.all([
        api.listBooks({ category: bookCategory !== "All" ? bookCategory : undefined }),
        api.listVideos({ category: videoCategory !== "All" ? videoCategory : undefined }),
      ]);
      setBooks(nextBooks);
      setVideos(nextVideos);
      setLoading(false);
    })();
  }, [bookCategory, videoCategory]);

  function switchTab(nextTab: Tab) {
    setTab(nextTab);
    setSearchParams({ tab: nextTab });
  }

  async function openBook(book: Book) {
    setSelectedBook(book);
    const { reviews: nextReviews } = await api.getBook(book.slug);
    setReviews(nextReviews);
  }

  return (
    <div className="cap-page">
      <section className="cap-hero">
        <div className="container">
          <div className="cap-hero__shell cap-hero__shell--split">
            <div>
              <span className="cap-hero__badge">Wisdom Library</span>
              <h1 className="display-1 cap-hero__title">Books, talks, and sacred media</h1>
              <p className="cap-hero__subtitle">
                A curated library for the spiritual path, from reflective reading to contemplative video.
              </p>
              <div className="hero-actions">
                <Link to="/teachers" className="btn btn--gold">Meet the guides</Link>
                <Link to="/stories" className="btn btn--outline">Read seeker stories</Link>
              </div>
            </div>

            <aside className="cap-hero__aside">
              <div className="cap-hero__card">
                <div className="cap-stat-grid">
                  <div className="cap-stat"><span className="cap-stat__value">{books.length || 12}</span><span className="cap-stat__label">books</span></div>
                  <div className="cap-stat"><span className="cap-stat__value">{videos.length || 8}</span><span className="cap-stat__label">videos</span></div>
                  <div className="cap-stat"><span className="cap-stat__value">7</span><span className="cap-stat__label">reading moods</span></div>
                  <div className="cap-stat"><span className="cap-stat__value">24/7</span><span className="cap-stat__label">available</span></div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="section section--surface-alt">
        <div className="container">
          <div className="cap-toolbar" style={{ marginBottom: "2rem" }}>
            <div className="cap-filter-row">
              {(["books", "videos"] as Tab[]).map((nextTab) => (
                <button key={nextTab} onClick={() => switchTab(nextTab)} className={`cap-chip ${tab === nextTab ? "cap-chip--active" : ""}`}>
                  {nextTab}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <p className="text-muted body-lg text-center" style={{ padding: "4rem 0" }}>Loading…</p>
          ) : tab === "books" ? (
            <BooksTab
              books={books}
              categories={BOOK_CATEGORIES}
              selected={bookCategory}
              onCategoryChange={setBookCategory}
              onOpenBook={openBook}
            />
          ) : (
            <VideosTab
              videos={videos}
              categories={VIDEO_CATEGORIES}
              selected={videoCategory}
              onCategoryChange={setVideoCategory}
            />
          )}
        </div>
      </section>

      {selectedBook && (
        <BookDetailPanel book={selectedBook} reviews={reviews} onClose={() => setSelectedBook(null)} />
      )}
    </div>
  );
}

function BooksTab({ books, categories, selected, onCategoryChange, onOpenBook }: {
  books: Book[];
  categories: string[];
  selected: string;
  onCategoryChange: (category: string) => void;
  onOpenBook: (book: Book) => void;
}) {
  return (
    <div style={{ display: "grid", gap: "1.5rem" }}>
      <div className="cap-filter-row">
        {categories.map((category) => (
          <button key={category} onClick={() => onCategoryChange(category)} className={`cap-chip ${selected === category ? "cap-chip--active" : ""}`}>
            {category}
          </button>
        ))}
      </div>

      <div className="cap-grid cap-grid--library">
        {books.map((book) => (
          <button key={book.id} onClick={() => onOpenBook(book)} className="cap-card" style={{ textAlign: "left", padding: 0 }}>
            <div className="cap-card__media" style={{ minHeight: "15rem" }}>
              {book.cover_url ? <img src={book.cover_url} alt={book.title} /> : <span aria-hidden="true">📖</span>}
            </div>
            <div className="cap-card__body">
              <h3 className="heading-md">{book.title}</h3>
              <p className="text-muted body-sm">{book.author}</p>
              {book.rating && (
                <p className="body-sm" style={{ color: "#b45309" }}>
                  {"★".repeat(Math.round(book.rating))} {book.rating.toFixed(1)}
                </p>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function VideosTab({ videos, categories, selected, onCategoryChange }: {
  videos: WisdomVideo[];
  categories: string[];
  selected: string;
  onCategoryChange: (category: string) => void;
}) {
  return (
    <div style={{ display: "grid", gap: "1.5rem" }}>
      <div className="cap-filter-row">
        {categories.map((category) => (
          <button key={category} onClick={() => onCategoryChange(category)} className={`cap-chip ${selected === category ? "cap-chip--active" : ""}`}>
            {category}
          </button>
        ))}
      </div>

      <div className="cap-grid cap-grid--teachers">
        {videos.map((video) => (
          <div key={video.id} className="cap-card">
            <div className="cap-card__media" style={{ minHeight: "14rem" }}>
              {video.thumbnail_url ? <img src={video.thumbnail_url} alt={video.title} /> : <span aria-hidden="true">🎬</span>}
            </div>
            <div className="cap-card__body">
              <div className="cap-card__meta-row">
                <span className="cap-pill cap-pill--gold">{video.access}</span>
                <span className="cap-pill" style={{ background: "rgba(61, 43, 31, 0.06)", color: "var(--c-muted)" }}>{video.category}</span>
              </div>
              <h3 className="heading-md">{video.title}</h3>
              {video.teacher_name && <p className="text-muted body-sm">By {video.teacher_name}</p>}
              <p className="body-sm" style={{ color: "var(--c-ink-75)" }}>{video.description}</p>
              <div className="cap-card__meta-row" style={{ marginTop: "auto", color: "var(--c-muted)", fontSize: "0.82rem" }}>
                <span>⏱ {Math.round(video.duration_seconds / 60)} min</span>
                <span>👁 {video.view_count.toLocaleString()}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BookDetailPanel({ book, reviews, onClose }: { book: Book; reviews: BookReview[]; onClose: () => void }) {
  const [reviewName, setReviewName] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewBody, setReviewBody] = useState("");
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmitReview(e: React.FormEvent) {
    e.preventDefault();
    await api.submitBookReview(book.id, { reviewer_name: reviewName, rating: reviewRating, body: reviewBody });
    setSubmitted(true);
  }

  return (
    <div className="cap-story-modal">
      <div className="cap-story-modal__sheet">
        <div className="cap-story-modal__header">
          <h2 className="heading-lg">Book detail</h2>
          <button onClick={onClose} className="btn btn--ghost">Close</button>
        </div>
        <div className="cap-story-modal__body" style={{ display: "grid", gap: "1.5rem" }}>
          <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
            <div className="cap-card__media" style={{ width: "7rem", height: "10rem", minHeight: 0, borderRadius: "1rem", flexShrink: 0 }}>
              {book.cover_url ? <img src={book.cover_url} alt={book.title} /> : <span aria-hidden="true">📖</span>}
            </div>
            <div style={{ minWidth: 0 }}>
              <h3 className="heading-lg">{book.title}</h3>
              <p className="text-muted body-sm">{book.author}</p>
              {book.rating && <p className="body-sm" style={{ color: "#b45309" }}>{"★".repeat(Math.round(book.rating))} {book.rating.toFixed(1)} ({book.review_count} reviews)</p>}
              {book.publication_year && <p className="text-muted body-sm">{book.publication_year}</p>}
              <div className="hero-actions" style={{ marginTop: "0.85rem" }}>
                {book.is_free_excerpt && book.excerpt_url && (
                  <a href={book.excerpt_url} target="_blank" rel="noopener noreferrer" className="btn btn--outline">Read excerpt</a>
                )}
                {book.purchase_url && (
                  <a href={book.purchase_url} target="_blank" rel="noopener noreferrer" className="btn btn--gold">Purchase</a>
                )}
              </div>
            </div>
          </div>

          <p className="body-md" style={{ color: "var(--c-ink-75)", lineHeight: 1.8 }}>{book.description}</p>

          <section className="cap-panel">
            <div className="cap-panel__header"><h3 className="heading-md">Reviews ({reviews.length})</h3></div>
            <div className="cap-panel__body">
              {reviews.length === 0 ? (
                <p className="text-muted body-sm">No reviews yet. Be the first.</p>
              ) : (
                <div className="cap-list">
                  {reviews.map((review) => (
                    <div key={review.id} className="cap-list__item" style={{ alignItems: "flex-start" }}>
                      <span className="cap-list__index">★</span>
                      <div>
                        <div className="heading-md" style={{ fontSize: "1rem" }}>{review.reviewer_name}</div>
                        <p className="body-sm" style={{ color: "var(--c-ink-75)" }}>{review.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="cap-panel">
            <div className="cap-panel__header"><h3 className="heading-md">Write a review</h3></div>
            <div className="cap-panel__body">
              {submitted ? (
                <p className="text-muted body-sm">Thank you. Your review is under moderation.</p>
              ) : (
                <form onSubmit={handleSubmitReview} style={{ display: "grid", gap: "1rem" }}>
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input value={reviewName} onChange={(e) => setReviewName(e.target.value)} required placeholder="Your name" className="form-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Rating</label>
                    <div className="cap-card__meta-row">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button key={rating} type="button" onClick={() => setReviewRating(rating)} className={`cap-chip ${rating <= reviewRating ? "cap-chip--active" : ""}`}>
                          ★ {rating}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Review</label>
                    <textarea value={reviewBody} onChange={(e) => setReviewBody(e.target.value)} required minLength={10} rows={4} placeholder="Share your thoughts about this book…" className="form-input" />
                  </div>
                  <button type="submit" className="btn btn--gold">Submit review</button>
                </form>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
